import React, { useEffect, useState } from "react";
import { useCart } from "../hooks/cartContext";
import { useAuthState } from "~/hooks/authContext";
import { navigate } from "gatsby";
import { useLocation } from "@reach/router";
import { useApiBaseUrl, useApiMutation } from "~/hooks/apiContext";
import { useClassWalletVendorId } from "~/hooks/classWalletContext";
import {
  getClassWalletSession,
  deleteClassWalletSession,
} from "~/utils/classWalletSession";
import Login from "../features/dashboard/Login";
import { PRODUCT_CATALOG } from "~/productCatalog";
import { PopulateClassWalletSessionItemsInput } from "~/backendApi/populateClassWalletSessionItems";
import "./CartDrawer.css";

function CartDrawer() {
  const { items, removeItem, clearCart } = useCart();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  // const [CWloginOpen, setCWLoginOpen] = useState(false);

  const authState = useAuthState();
  const apiBaseUrl = useApiBaseUrl();

  const classWalletVendorId = useClassWalletVendorId();

  const classWalletSession = getClassWalletSession();
  const isClassWalletOrder = !!classWalletSession;
  console.log("ClassWallet Order:", isClassWalletOrder);

  // const enrichedItems = items.map((item) => ({
  //   ...item,
  //   ...PRODUCT_CATALOG[item.id],
  // }));

  const [isCheckingOut, setIsCheckingOut] = useState(false);

  const { mutateAsync: createPaymentCheckoutSession } = useApiMutation<
    any,
    string
  >(async (client, args) => client.createPaymentCheckoutSession(args));

  const { mutateAsync: populateClassWalletSessionItems } = useApiMutation(
    (client, args: PopulateClassWalletSessionItemsInput) =>
      client.populateClassWalletSessionItems(args),
  );

  // Toggle cart drawer visibility
  useEffect(() => {
    const toggle = () => setIsOpen((prev) => !prev);
    document.addEventListener("toggle-cart", toggle);
    return () => document.removeEventListener("toggle-cart", toggle);
  }, []);

  // Auto open if returning from checkout flow
  const isSignedIn = authState.type === "signedIn";
  const params = new URLSearchParams(location.search);
  const fromCart = params.has("fromCart");
  useEffect(() => {
    if (isSignedIn && fromCart) {
      document.dispatchEvent(new Event("toggle-cart"));
    }
  }, [isSignedIn, fromCart]);

  // 🧩 Open ClassWallet login/signup
  // useEffect(() => {
  //   if (CWloginOpen) {
  //     navigate("/signup?fromCart=true");
  //   }
  // }, [CWloginOpen]);

  // 🧩 Build ClassWallet Checkout URL
  const getClassWalletCheckoutUrl = () => {
    if (!classWalletSession) return null;
    const callbackUrl = new URL(
      `${apiBaseUrl}/class-wallet/order-sessions/${classWalletSession.orderId}`,
    );
    const checkoutUrl = new URL("https://app.classwallet.com/payby-checkout/");
    checkoutUrl.searchParams.set("callback", callbackUrl.toString());
    checkoutUrl.searchParams.set("vendorId", classWalletVendorId);
    return checkoutUrl.toString();
  };

  // 🧾 Handle Checkout (both flows)
  const handleCheckout = async () => {
    if (isCheckingOut) return; // safety
    if (items.length === 0) return;

    setIsCheckingOut(true);

    if (isClassWalletOrder) {
      try {
        if (!classWalletSession) {
          throw new Error("Missing ClassWallet session");
        }

        await populateClassWalletSessionItems({
          orderId: classWalletSession.orderId,
          items: items.map((item) => ({
            priceLookupKey: item.id,
            quantity: item.quantity,
          })),
        });

        const cwUrl = getClassWalletCheckoutUrl();
        if (cwUrl) {
          deleteClassWalletSession();
          window.location.href = cwUrl;
          return;
        }

        throw new Error("Could not create ClassWallet checkout URL");
      } catch (e) {
        console.error("ClassWallet checkout error:", e);
        setIsCheckingOut(false);
        return;
      }
    }

    try {
      const lineItems = items.map((i) => ({
        priceLookupKey: i.id,
        quantity: i.quantity,
      }));

      const redirectUrl = await createPaymentCheckoutSession({
        items: lineItems,
        successUrl: `${window.location.origin}/checkout/success/`,
        cancelUrl: `${window.location.origin}/checkout/cancel`,
      });

      window.location.href = redirectUrl;
    } catch (e) {
      console.error("Checkout error:", e);
      setIsCheckingOut(false); // allow retry if something fails
    }
  };
  // --- UI ---
  return (
    <>
      <div className={`cart-drawer ${isOpen ? "open" : ""}`}>
        <div className="cart-header">
          <h2>Your Cart</h2>
          <button onClick={() => setIsOpen(false)}>✖</button>
        </div>

        {items.length === 0 ? (
          <p>Cart is empty</p>
        ) : (
          <ul className="cart-items">
            {items.map((item) => {
              const product = PRODUCT_CATALOG[item.id];
              if (!product) return null;

              const lineTotal =
                product.price != null ? product.price * item.quantity : null;

              return (
                <li key={item.id} className="cart-row">
                  {/* Column 1: Name + Qty */}
                  <button
                    className="cart-col-remove"
                    onClick={() => removeItem(item.id)}
                    aria-label={`Remove ${product.name}`}
                  >
                    ✕
                  </button>

                  <div className="cart-col-name">
                    <div className="cart-item-name">{product.name}</div>
                    <div className="cart-item-qty">Qty: {item.quantity}</div>
                  </div>

                  {/* Column 2: Delete */}

                  {/* Column 3: Price */}

                  <div className="cart-col-price">
                    {lineTotal != null && <>${lineTotal.toFixed(2)}</>}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        <div className="cart-summary">
          <div className="cart-subtotal">
            <span className="cart-subtotal-label">Subtotal</span>
            <span>
              $
              {items
                .reduce(
                  (sum, i) =>
                    i.price != null ? sum + i.price * i.quantity : sum,
                  0,
                )
                .toFixed(2)}
            </span>
          </div>
        </div>
        {items.length > 0 && (
          <div className="cart-actions">
            {authState.type === "signedIn" ? (
              <>
                <button onClick={handleCheckout} disabled={isCheckingOut}>
                  {isCheckingOut
                    ? "Redirecting to secure checkout…"
                    : isClassWalletOrder
                      ? "Checkout with ClassWallet"
                      : "Checkout"}
                </button>

                {!isCheckingOut && (
                  <p className="cart-payment-note">
                    {isClassWalletOrder
                      ? "Please complete payment using ClassWallet."
                      : "Secure checkout powered by Stripe."}
                  </p>
                )}
              </>
            ) : (
              <div className="login-prompt">
                <p>Please sign in to continue checkout.</p>
                <button
                  onClick={() =>
                    navigate(
                      isClassWalletOrder
                        ? "/signup?fromCart=true"
                        : "/dashboard/?fromCart=true",
                    )
                  }
                >
                  Log In / Sign Up
                </button>
              </div>
            )}
          </div>
        )}

        {items.length > 0 && (
          <div className="cart-actions">
            <button onClick={clearCart}>Clear Cart</button>
          </div>
        )}
      </div>

      {loginOpen && (
        <Login
          hideSsoButtons
          onClose={() => setLoginOpen(false)}
          onSuccess={() => setLoginOpen(false)}
        />
      )}

      {/*CWloginOpen && <ClassWalletSignUpPage /> Adding useEffect instead for if(CWLoginOpen) */}
    </>
  );
}

export default CartDrawer;
